// Activo Fijo → Depreciación → Corrida de depreciación (preview + contabilizar).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator, Loader2, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, DeprecItem, afRpc, mesesTr, fieldCls, money } from './shared';

const now = new Date();
export function DepreciacionAF() {
  const { t } = useTranslation('activofijo');
  const MESES = mesesTr();
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [anio, setAnio] = useState(now.getFullYear());
  const [mes, setMes] = useState(now.getMonth() + 1); // mes en curso
  const [preview, setPreview] = useState<{ items: DeprecItem[]; total: number } | null>(null);

  const previsualizar = useMutation({
    mutationFn: () => afRpc<{ items: DeprecItem[]; total: number }>('af_depreciacion_corrida', { _anio: anio, _mes: mes, _confirmar: false }),
    onSuccess: (r) => { setPreview(r); if (!r.items.length) toast.info(t('depreciacion.sin_dep_toast')); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const ejecutar = useMutation({
    mutationFn: () => afRpc<any>('af_depreciacion_corrida', { _anio: anio, _mes: mes, _confirmar: true }),
    onSuccess: (r) => {
      if (r.contabilizada) toast.success(t('depreciacion.ok_asiento', { mes: MESES[mes - 1], anio, n: r.n, total: money(r.total) }));
      else toast.warning(t('depreciacion.ok_sin_asiento', { n: r.n, total: money(r.total), aviso: r.aviso ?? t('depreciacion.revisa_mapeo') }));
      setPreview(null);
      void qc.invalidateQueries({ queryKey: ['af-activos'] });
      void qc.invalidateQueries({ queryKey: ['af-corridas'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Calculator className="w-5 h-5" /> {t('depreciacion.titulo')}</h1>
      <p className="text-sm text-muted-foreground">{t('depreciacion.intro')}</p>
      <Card>
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <Campo label={t('depreciacion.anio')}><input type="number" className={`${fieldCls} w-28`} value={anio} onChange={(e) => setAnio(Number(e.target.value))} /></Campo>
          <Campo label={t('depreciacion.mes')}><select className={`${fieldCls} w-40`} value={mes} onChange={(e) => setMes(Number(e.target.value))}>{MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select></Campo>
          <Button variant="outline" isLoading={previsualizar.isPending} onClick={() => previsualizar.mutate()}>{t('depreciacion.previsualizar')}</Button>
          <Button isLoading={ejecutar.isPending} disabled={!preview || preview.items.length === 0}
            onClick={async () => { if (await dlg.confirmar(t('depreciacion.confirmar', { mes: MESES[mes - 1], anio }))) ejecutar.mutate(); }}>
            <PlayCircle className="w-4 h-4 mr-1" /> {t('depreciacion.ejecutar')}
          </Button>
        </CardContent>
      </Card>

      {previsualizar.isPending && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {preview && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t('depreciacion.preview_titulo', { mes: MESES[mes - 1], anio, n: preview.items.length, total: money(preview.total) })}</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">{t('depreciacion.col_placa')}</th><th className="py-2 pr-3">{t('depreciacion.col_activo')}</th><th className="py-2 pr-3">{t('depreciacion.col_categoria')}</th><th className="py-2 pr-3 text-right">{t('depreciacion.col_base')}</th><th className="py-2 pr-3 text-right">{t('depreciacion.col_previa')}</th><th className="py-2 pr-3 text-right">{t('depreciacion.col_cuota')}</th><th className="py-2 pr-3 text-right">{t('depreciacion.col_acumulada')}</th>
              </tr></thead>
              <tbody>
                {preview.items.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{t('depreciacion.vacio')}</td></tr>}
                {preview.items.map((d) => (
                  <tr key={`${d.activo_id}-${d.componente_id ?? "resto"}`} className="border-b last:border-0">
                    <td className="py-2 pr-3 font-mono">{d.placa}</td>
                    <td className="py-2 pr-3">{d.nombre}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{d.categoria}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.base)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.acumulada_previa)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums font-medium">{money(d.importe)}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(d.acumulada_resultante)}</td>
                  </tr>
                ))}
              </tbody>
              {preview.items.length > 0 && (
                <tfoot><tr className="font-semibold border-t"><td className="py-2 pr-3" colSpan={5}>{t('depreciacion.total_periodo')}</td><td className="py-2 pr-3 text-right font-mono">{money(preview.total)}</td><td></td></tr></tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
